import { useCallback, useEffect, useRef, useState } from 'react';
import { seekSafely, useAudioUnlock } from './audioUnlock';

/**
 * Play an arbitrary `[from, to]` window of one already-loaded audio Blob.
 *
 * `useContextPlayer` does the same job for a segment inside a *task*, reading
 * the blob from IndexedDB itself and always padding by a fixed amount. The
 * trimmer already holds the original blob (it decoded it to draw the waveform)
 * and needs to play windows the user is dragging in real time, so it drives its
 * own element with the same machinery: one hidden <audio>, the iOS gesture
 * unlock, a seek instead of a re-encode, and a rAF/`timeupdate` watchdog that
 * pauses at the end of the window.
 *
 * Nothing is re-encoded and nothing is cached globally: the object URL lives
 * exactly as long as the hook.
 */
export interface RangePlayerState {
  /** Caller-chosen tag of the window that is playing ('selection' | …), or null. */
  mode: string | null;
  /** True while audio is actually playing. */
  isPlaying: boolean;
  error: string | null;
  /** Play `[fromSec, toSec]`. Call it straight from a tap so iOS allows it. */
  play: (fromSec: number, toSec: number, mode: string) => Promise<void>;
  /** Stop. Safe to call when nothing is playing. */
  stop: () => void;
  /** The underlying element — used to read `currentTime` for the playhead. */
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

/** Give up waiting for `loadedmetadata` after this and play anyway. */
const METADATA_TIMEOUT_MS = 5000;

/** Resolve once the element can be seeked (or we've waited long enough). */
function whenSeekable(el: HTMLAudioElement): Promise<void> {
  if (el.readyState >= 1 /* HAVE_METADATA */) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let timer = 0;
    const finish = () => {
      el.removeEventListener('loadedmetadata', finish);
      el.removeEventListener('error', finish);
      window.clearTimeout(timer);
      resolve();
    };
    timer = window.setTimeout(finish, METADATA_TIMEOUT_MS);
    el.addEventListener('loadedmetadata', finish);
    el.addEventListener('error', finish);
  });
}

export function useRangePlayer(blob: Blob | null): RangePlayerState {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const unlockedRef = useRef(false);
  const unlockingRef = useRef(false);
  /** Where the current window ends, in original-file seconds. */
  const stopAtRef = useRef(Number.POSITIVE_INFINITY);
  /** Bumped on every play()/stop() so stale async continuations bail out. */
  const tokenRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const playingRef = useRef(false);

  const [mode, setMode] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // one <audio> element for the lifetime of the hook
  if (audioRef.current === null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'auto';
  }

  useAudioUnlock(audioRef, unlockedRef, unlockingRef);

  const halt = useCallback(() => {
    tokenRef.current += 1;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    stopAtRef.current = Number.POSITIVE_INFINITY;
    playingRef.current = false;
    const el = audioRef.current;
    if (el) el.pause();
    setIsPlaying(false);
    setMode(null);
  }, []);

  /* ---------------- element events ---------------- */

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    // The silent unlock drives the same element, so its events must not be
    // mistaken for a real window.
    const handlePlay = () => {
      if (!unlockingRef.current && playingRef.current) setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      if (unlockingRef.current || !playingRef.current) return;
      halt();
    };
    // Backstop for the rAF watchdog: rAF is throttled to ~0 fps in a
    // backgrounded tab, `timeupdate` still fires while audio plays.
    const handleTimeUpdate = () => {
      if (!playingRef.current) return;
      if (el.currentTime >= stopAtRef.current) halt();
    };
    const handleError = () => {
      if (unlockingRef.current) return;
      if (!el.getAttribute('src')) return; // teardown, not a failure
      setError('Could not play the original audio for this task.');
      halt();
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('error', handleError);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('timeupdate', handleTimeUpdate);
      el.removeEventListener('error', handleError);
    };
  }, [halt]);

  /* ---------------- object URL follows the blob ---------------- */

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    /**
     * Detach the element from the current object URL *before* revoking it.
     * Revoking a URL that is still the element's `src` leaves a dead source
     * behind, and any play() that lands in the gap fires a spurious `error`.
     */
    const release = () => {
      if (urlRef.current && el.getAttribute('src') === urlRef.current) {
        el.removeAttribute('src');
        el.load();
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };

    if (!blob) {
      el.pause();
      release();
      return;
    }

    release();
    const url = URL.createObjectURL(blob);
    urlRef.current = url;
    el.src = url;

    return () => {
      el.pause();
      release();
    };
  }, [blob]);

  // stop playback on unmount — the trimmer's audio must never outlive it
  useEffect(
    () => () => {
      tokenRef.current += 1;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      playingRef.current = false;
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    },
    [],
  );

  /* ---------------- play ---------------- */

  const play = useCallback(
    async (fromSec: number, toSec: number, nextMode: string) => {
      const el = audioRef.current;
      if (!el || !el.getAttribute('src')) return;

      // A tap is a user gesture; from here on the element is blessed, and the
      // silent unlock (which may be mid-flight) must not stop what we start.
      unlockedRef.current = true;
      unlockingRef.current = false;
      el.muted = false;

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.pause();
      const token = ++tokenRef.current;
      const stale = () => token !== tokenRef.current;

      setError(null);
      setMode(nextMode);
      playingRef.current = true;

      const from = Math.max(0, fromSec);
      stopAtRef.current = Math.max(from, toSec);

      /** Clamp the window to the real file length once we know it. */
      const clamp = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          stopAtRef.current = Math.min(el.duration, Math.max(from, toSec));
        }
      };

      if (el.readyState >= 1) {
        clamp();
        seekSafely(el, from);
        try {
          await el.play();
        } catch {
          if (!stale()) halt();
          return;
        }
      } else {
        // Metadata isn't in yet and we must not spend the gesture waiting for
        // it: start muted at 0, then seek into place the moment we can.
        el.muted = true;
        const started = el.play().catch(() => 'rejected' as const);
        await whenSeekable(el);
        if (stale()) {
          el.muted = false;
          return;
        }
        clamp();
        seekSafely(el, from);
        el.muted = false;
        const outcome = await started;
        if (stale()) return;
        if (outcome === 'rejected' && el.paused) {
          try {
            await el.play();
          } catch {
            if (!stale()) halt();
            return;
          }
        }
      }

      if (stale()) return;
      setIsPlaying(true);

      /**
       * Stop at the end of the window. `timeupdate` alone fires only every
       * ~250 ms, which would overshoot a 300 ms word by most of its length, so
       * rAF drives the common case and `timeupdate` covers backgrounded tabs.
       */
      const tick = () => {
        rafRef.current = null;
        if (stale() || !playingRef.current) return;
        if (el.paused) return;
        if (el.currentTime >= stopAtRef.current) {
          halt();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [halt],
  );

  return { mode, isPlaying, error, play, stop: halt, audioRef };
}
